import React, { useState } from 'react'
import { Card } from 'react-bootstrap'
import { Link } from 'react-router-dom'

import FAIcon from '../components/FAIcon'
import format from '../services/format'
import GeneralTasksService from '../services/GeneralTasksService'
import TasksService from '../services/TasksService'
import Permissions, { CAPABILITIES } from '../utils/Permissions'

import './TaskCard.scss'

const TaskCard = ({
  incompleteTasks = [],
  completeTasks = [],
  cases,
  getValue,
  caseNumber,
  handleButtonClick,
  isDashboardView = false,
  user,
  onTaskDeleted,
}) => {
  const [deleteError, setDeleteError] = useState('')
  const [forceWarning, setForceWarning] = useState(null) // { taskNumber, message }
  const [deletedIds, setDeletedIds] = useState(new Set())
  const [deletingIds, setDeletingIds] = useState(new Set())

  const getFormattedDateValue = dateUtc => {
    if (dateUtc) {
      return format.date(dateUtc)
    } else {
      return ''
    }
  }

  function getClientInfo(task) {
    // eslint-disable-next-line
    var fullName = cases.map(caseObj => {
      if (caseObj.caseNumber === task.caseNumber) {
        return `${getValue('client.firstName', '', caseObj)} ${getValue('client.lastName', '', caseObj)}`
      } else {
        return ''
      }
    })
    return (
      <p className="text-truncate">
        {fullName} <span>&#183;</span> Case #: {task.caseNumber}
      </p>
    )
  }

  const visibleIncompleteTasks = [...(incompleteTasks ?? [])]
    .sort((a, b) => new Date(a.dueDateUtc) - new Date(b.dueDateUtc))
    .filter(t => !deletedIds.has(t.taskNumber ?? t.id))

  const orderedCompleteTasks = completeTasks?.sort((a, b) => {
    return new Date(b.dueDateUtc) - new Date(a.dueDateUtc)
  })

  const canDelete = Permissions.can(user, CAPABILITIES.DELETE_TASK)

  const handleDeleteClick = async (e, task) => {
    e.preventDefault()
    e.stopPropagation()
    setDeleteError('')
    const isGeneralTask = !task.caseNumber
    const taskId = isGeneralTask ? task.id : task.taskNumber
    setDeletingIds(prev => new Set([...prev, taskId]))
    try {
      if (isGeneralTask) {
        await GeneralTasksService.delete(task.id)
      } else {
        await TasksService.deleteTask(task.caseNumber, task.taskNumber)
      }
      setDeletedIds(prev => new Set([...prev, taskId]))
      if (onTaskDeleted) onTaskDeleted(taskId)
    } catch (err) {
      setDeletingIds(prev => {
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
      if (err.requiresForce) {
        setForceWarning({
          taskNumber: task.taskNumber,
          caseNumber: task.caseNumber,
          message: 'This task has been filed on a Progress Report.',
        })
      } else {
        setDeleteError(err.message)
      }
    }
  }

  const handleForceDelete = async () => {
    if (!forceWarning) return
    setDeleteError('')
    const taskId = forceWarning.taskNumber
    setDeletingIds(prev => new Set([...prev, taskId]))
    try {
      await TasksService.deleteTask(forceWarning.caseNumber, forceWarning.taskNumber, true)
      setDeletedIds(prev => new Set([...prev, forceWarning.taskNumber]))
      if (onTaskDeleted) onTaskDeleted(forceWarning.taskNumber)
      setForceWarning(null)
    } catch (err) {
      setDeletingIds(prev => {
        const next = new Set(prev)
        next.delete(taskId)
        return next
      })
      setDeleteError(err.message)
      setForceWarning(null)
    }
  }

  return (
    <React.Fragment>
      {forceWarning && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Task</h5>
              </div>
              <div className="modal-body">
                <p>{forceWarning.message}</p>
                <p>Are you sure you want to permanently delete this task and all associated data?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setForceWarning(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleForceDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {visibleIncompleteTasks.length >= 1 || orderedCompleteTasks.length >= 1 ? (
        <Card className="shadow task-card">
          <Card.Body className="p-0">
            {!!deleteError && <div className="text-danger small p-2">{deleteError}</div>}
            {visibleIncompleteTasks.map((task, index) => {
              return (
                <div key={index} className="task cardrow d-flex align-items-stretch">
                  <Link
                    to={
                      task.caseNumber
                        ? `/cases/${task.caseNumber}/tasks/${task.taskNumber}`
                        : `/tasks/${task.id || task.Id}`
                    }
                    className="flex-grow-1"
                    style={{ overflow: 'hidden' }}
                  >
                    <div className="d-flex">
                      <div className="row w-100 m-0">
                        <div className="inline-circle align-self-center col-1">
                          <span title="Mark task complete">
                            <FAIcon name="circle" type="far fa-md check-box" />
                          </span>
                        </div>
                        {isDashboardView ? (
                          <React.Fragment>
                            <div className="col-7">
                              <div className="title-text text-truncate">{task.title}</div>
                              {task.caseNumber && <div className="client-case-line">{getClientInfo(task)}</div>}
                            </div>
                            <div className="col-3">
                              <div className="date text-primary text-align-right">
                                <i>{getFormattedDateValue(task.dueDateUtc)}</i>
                              </div>
                            </div>
                          </React.Fragment>
                        ) : (
                          <React.Fragment>
                            <div className="col-7">
                              <div className="title-text text-truncate">{task.title}</div>
                            </div>
                            <div className="col-3">
                              <div className="date text-primary text-align-right">
                                <i>{getFormattedDateValue(task.dueDateUtc)}</i>
                              </div>
                            </div>
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                  </Link>
                  {canDelete && (
                    <span
                      title="Delete this task"
                      role="button"
                      tabIndex={0}
                      className="align-self-center px-2"
                      style={{ cursor: 'pointer', color: '#dc3545', flexShrink: 0 }}
                      onClick={e =>
                        !deletingIds.has(task.caseNumber ? task.taskNumber : task.id) && handleDeleteClick(e, task)
                      }
                      onKeyDown={e =>
                        e.key === 'Enter' &&
                        !deletingIds.has(task.caseNumber ? task.taskNumber : task.id) &&
                        handleDeleteClick(e, task)
                      }
                    >
                      {deletingIds.has(task.caseNumber ? task.taskNumber : task.id) ? (
                        <span
                          className="spinner-border spinner-border-sm text-danger"
                          role="status"
                          aria-hidden="true"
                          style={{ width: '1rem', height: '1rem' }}
                        />
                      ) : (
                        <FAIcon name="trash-alt" type="far fa-md" />
                      )}
                    </span>
                  )}
                </div>
              )
            })}
            {orderedCompleteTasks.map((task, index) => {
              return (
                <div key={index} className="task cardrow">
                  <Link
                    to={
                      task.caseNumber
                        ? `/cases/${task.caseNumber}/tasks/${task.taskNumber}`
                        : `/tasks/${task.id || task.Id}`
                    }
                  >
                    <div className="d-flex">
                      <div className="row w-100 m-0">
                        <div className="inline-circle align-self-center col-1">
                          <FAIcon name="check-circle" type="far fa-md check-box" />
                        </div>
                        <React.Fragment>
                          <div className="col-7">
                            <div className="title-text text-truncate">
                              <s>{task.title}</s>
                            </div>
                          </div>
                          <div className="col-3">
                            <div className="date text-primary text-align-right">
                              <i>
                                <s>{getFormattedDateValue(task.dueDateUtc)}</s>
                              </i>
                            </div>
                          </div>
                        </React.Fragment>
                      </div>
                    </div>
                  </Link>
                </div>
              )
            })}
          </Card.Body>
          <Card.Footer className="p-2">
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => handleButtonClick(caseNumber ? `/cases/${caseNumber}/tasks` : '/tasks/new')}
            >
              Add a Task
            </button>
          </Card.Footer>
        </Card>
      ) : (
        <Card className="shadow task-card-container mb-3">
          <Card.Body>
            <p className="text-center">
              <strong>No current tasks to complete</strong>
            </p>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => handleButtonClick(caseNumber ? `/cases/${caseNumber}/tasks` : '/tasks/new')}
            >
              Add a Task
            </button>
          </Card.Body>
        </Card>
      )}
    </React.Fragment>
  )
}

export default TaskCard
